import config from "appConfig";

const theory = `<h3 class=sectionHead>Introduction</h3>
<p>The signals of several microphones can be used together to obtain an output adapted to the problem at hand. That is, for example, favouring a particular source over others by using a spatial filtering, localizing the loudest zones of a sound source by plotting intensity maps showing the spatial variations of an acoustical data (pressure, velocity or intensity), monitoring environmental sounds, calculate relevant indicators (radiated acoustical power, directivity patterns of sound sources, etc.). To do this, the microphones are grouped in arrays or clusters and their output signals are processed to match in the intended application. In this Lesson, we will present the principles of two classical processing methods: delay & sum [3, 1] and differential array [2] beamformings.
<h3 class=sectionHead>Delay & sum beamforming</h3>
<p>
   Let us consider a linear array made of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>N</mi>
   </math>
   sensors denoted
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   and depicted by blue bullets (<span id=textcolor1 style="color: #0A82C6">
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mo class=MathClass-bin>∙</mo>
      </math>
   </span>) in <a href=# data-img-alt="Figure 1. Microphone array geometry." data-img-src=${config.ASSETS_URL}/04/27/theory/figure-1.svg>Figure 1</a>. Adjacent sensors are separated from a distance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>d</mi>
   </math>. To simplify calculations, let is choose
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>N</mi>
   </math>
   to be odd, then we have
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>−</mo>
      <mo class=MathClass-open>(</mo>
      <mi>N</mi>
      <mo class=MathClass-bin>−</mo>
      <mn>1</mn>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
      <mo class=MathClass-rel>≤</mo>
      <mi>n</mi>
      <mo class=MathClass-rel>≤</mo>
      <mo class=MathClass-open>(</mo>
      <mi>N</mi>
      <mo class=MathClass-bin>−</mo>
      <mn>1</mn>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
   </math>. Distances between the sound source and microphone
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>n</mi>
   </math>
   are denoted
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>. The distance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>δ</mi>
   </math>
   is given by the sine law
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>δ</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>d</mi>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>. Then, for a source located at a large distance from the array (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≫</mo>
      <mi>d</mi>
      <mo class=MathClass-open>(</mo>
      <mi>N</mi>
      <mo class=MathClass-bin>−</mo>
      <mn>1</mn>
      <mo class=MathClass-close>)</mo>
   </math>), we get
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≃</mo>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi>δ</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi>d</mi>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>. With the same method we obtain:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mn>2</mn>
      <mi>δ</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mn>2</mn>
      <mi>d</mi>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>, that can be generalised to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi mathvariant=italic>nδ</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi mathvariant=italic>nd</mi>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 1. Microphone array geometry." src=${config.ASSETS_URL}/04/27/theory/figure-1.svg><figcaption class=caption>Figure 1. Microphone array geometry.</figcaption></figure>
<p>
   Assuming a monopole source with a volume velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>Q</mi>
   </math>
   located in
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>S</mi>
   </math>, the pressure on each microphone is given by
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jkρcQ</mi>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <msub>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   If all microphones have the same sensitivity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>μ</mi>
         </mrow>
      </msub>
   </math>, their output voltage
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   is equal to
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>μ</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jkρcQ</mi>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>μ</mi>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <msub>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mi>A</mi>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <msub>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>A</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>jkρcQ</mi>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>μ</mi>
         </mrow>
      </msub>
   </math>
   is a complex amplitude term. By replacing the expression of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>, we get
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi>A</mi>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <mo class=MathClass-open>(</mo>
                        <msub>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <mi mathvariant=italic>nδ</mi>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>nδ</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   Since the source is at a large distance, we can use a zero-order approximation for the
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>A</mi>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   term, since the function
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <mi>r</mi>
   </math>
   decreases slowly for large values of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>r</mi>
   </math>:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi mathvariant=italic>nδ</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>≃</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>. For the phase term
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>jk</mi>
            <msub>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
         </mrow>
      </msup>
   </math>, which can oscillate rapidly at high frequency we keep
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi mathvariant=italic>nδ</mi>
   </math>
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi>A</mi>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <mo class=MathClass-open>(</mo>
                        <msub>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <mi mathvariant=italic>nδ</mi>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(4)
</table>
<p>Now, if we sum the output signal of all the microphones, we have
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <munder class=msub>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>N</mi>
               </mrow>
            </munder>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi>A</mi>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <msub>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <munder class=msub>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>N</mi>
               </mrow>
            </munder>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jknδ</mi>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   For calculating the directivity function
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>d</mi>
      <mo class=MathClass-open>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   of the array, we write
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>d</mi>
            <mo class=MathClass-open>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>0</mn>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   For
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>𝜃</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>δ</mi>
   </math>
   is null, then we get
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>NA</mi>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jk</mi>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>, which yields
<table class=equation>
   <tr>
      <td>
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>d</mi>
            <mo class=MathClass-open>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <munder class=msub>
                     <mrow>
                        <mo>∑</mo>
                     </mrow>
                     <mrow>
                        <mi>N</mi>
                     </mrow>
                  </munder>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jknδ</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mi>N</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   Using series properties, it can be shown that
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msubsup>
         <mrow>
            <mi class=MathClass-op>∑</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <mi>n</mi>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mo class=MathClass-open>(</mo>
            <mi>N</mi>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>∕</mo>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-open>(</mo>
            <mi>N</mi>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>∕</mo>
            <mn>2</mn>
         </mrow>
      </msubsup>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>jknδ</mi>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>Nkδ</mi>
            <mo class=MathClass-bin>∕</mo>
            <mn>2</mn>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kδ</mi>
            <mo class=MathClass-bin>∕</mo>
            <mn>2</mn>
            <mo class=MathClass-close>)</mo>
         </mrow>
      </mfrac>
   </math>, yielding the following expression for the directivity function
<table class=equation>
   <tr>
      <td style="font-size: 10px">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>d</mi>
            <mo class=MathClass-open>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>Nkδ</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mn>2</mn>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>N</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kδ</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mn>2</mn>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>Nkd</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mn>2</mn>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>N</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kd</mi>
                  <mo class=MathClass-bin>∕</mo>
                  <mn>2</mn>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   The directivity function
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>d</mi>
      <mo class=MathClass-open>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is plotted in <a href=# data-img-alt="Figure 2. Directivity function at 1 kHz with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>d</mi><mo class=MathClass-rel>=</mo><mn>5</mn></math> cm (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>d</mi><mo class=MathClass-bin>∕</mo><mi>λ</mi><mo class=MathClass-rel>≃</mo><mn>0</mn><mo class=MathClass-punc>.</mo><mn>15</mn></math>) for several <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>N</mi></math> values." data-img-src=${config.ASSETS_URL}/04/27/theory/figure-2.svg>Figure 2</a> at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>1</mn>
   </math>
   kHz for a microphone spacing of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mn>5</mn>
   </math>
   cm and for several values of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>N</mi>
   </math>. It can be seen that the main lobe becomes narrower as the number of microphones increases while the number of side lobes increases. Note that the angle of the first null value is calculated from
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>Nkd</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>2</mn>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   which yields
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>𝜃</mi>
      <mo class=MathClass-rel>=</mo>
      <mi class=qopname>arcsin</mi>
      <mo>⁡</mo>
      <mfrac>
         <mrow>
            <mn>2</mn>
            <mi>π</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>Nkd</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mi class=qopname>arcsin</mi>
      <mo>⁡</mo>
      <mfrac>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>Nfd</mi>
         </mrow>
      </mfrac>
   </math>.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 2. Directivity function at 1 kHz with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>d</mi><mo class=MathClass-rel>=</mo><mn>5</mn></math> cm (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>d</mi><mo class=MathClass-bin>∕</mo><mi>λ</mi><mo class=MathClass-rel>≃</mo><mn>0</mn><mo class=MathClass-punc>.</mo><mn>15</mn></math>) for several <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>N</mi></math> values." src=${config.ASSETS_URL}/04/27/theory/figure-2.svg><figcaption class=caption>Figure 2. Directivity function at 1 kHz with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>d</mi><mo class=MathClass-rel>=</mo><mn>5</mn></math> cm (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>d</mi><mo class=MathClass-bin>∕</mo><mi>λ</mi><mo class=MathClass-rel>≃</mo><mn>0</mn><mo class=MathClass-punc>.</mo><mn>15</mn></math>) for several <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>N</mi></math> values.</figcaption></figure>
<p>
   The frequency dependence of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>d</mi>
      <mo class=MathClass-open>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is highlighted in <a href=# data-img-alt="Figure 3. Directivity function of a line array of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>20</mn></math> microphones separated from <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>d</mi><mo class=MathClass-rel>=</mo><mn>5</mn></math> cm for several frequencies." data-img-src=${config.ASSETS_URL}/04/27/theory/figure-3.svg>Figure 3</a>. At low frequency, the array response is omnidirectional and becomes more selective when the frequency increases. The number of secondary lobes also increases with frequency. When the spacing between microphones is equal or larger than the wavelength (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>d</mi>
      <mo class=MathClass-rel>≤</mo>
      <mi>λ</mi>
   </math>), aliasing creates additional lobes having the same amplitude as the main lobe.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 3. Directivity function of a line array of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>20</mn></math> microphones separated from <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>d</mi><mo class=MathClass-rel>=</mo><mn>5</mn></math> cm for several frequencies." src=${config.ASSETS_URL}/04/27/theory/figure-3.svg><figcaption class=caption>Figure 3. Directivity function of a line array of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>20</mn></math> microphones separated from <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>d</mi><mo class=MathClass-rel>=</mo><mn>5</mn></math> cm for several frequencies.</figcaption></figure>
<p>
   The source of interest is not always located at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>𝜃</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>, it is then necessary to steer the array response to other angles. This can be done by compensating the time delays
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>τ</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   due to the propagation
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <munder class=msub>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>N</mi>
               </mrow>
            </munder>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi>A</mi>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <msub>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <munder class=msub>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>N</mi>
               </mrow>
            </munder>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jknδ</mi>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>τ</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                  </msub>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>τ</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>nd</mi>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <msub>
         <mrow>
            <mi>𝜃</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mi>c</mi>
   </math>, where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>𝜃</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is the angle of the source.
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   can be rewritten
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi>A</mi>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <msub>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mn>0</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <munder class=msub>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>N</mi>
               </mrow>
            </munder>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jknd</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <msub>
                     <mrow>
                        <mi>𝜃</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   Then, the directivity function for a linear array steering at angle
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>𝜃</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is given by
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math display=block class=equation xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>d</mi>
            <mo class=MathClass-open>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mrow>
                     <mo form=prefix fence=true>[</mo>
                     <mrow>
                        <mi mathvariant=italic>Nkd</mi>
                        <mo class=MathClass-bin>∕</mo>
                        <mn>2</mn>
                        <mrow>
                           <mo form=prefix fence=true>(</mo>
                           <mrow>
                              <mi class=qopname>sin</mi>
                              <mo>⁡</mo>
                              <mi>𝜃</mi>
                              <mo class=MathClass-bin>−</mo>
                              <mi class=qopname>sin</mi>
                              <mo>⁡</mo>
                              <msub>
                                 <mrow>
                                    <mi>𝜃</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>0</mn>
                                 </mrow>
                              </msub>
                           </mrow>
                           <mo form=postfix fence=true>)</mo>
                        </mrow>
                     </mrow>
                     <mo form=postfix fence=true>]</mo>
                  </mrow>
               </mrow>
               <mrow>
                  <mi>N</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mrow>
                     <mo form=prefix fence=true>[</mo>
                     <mrow>
                        <mi mathvariant=italic>kd</mi>
                        <mo class=MathClass-bin>∕</mo>
                        <mn>2</mn>
                        <mrow>
                           <mo form=prefix fence=true>(</mo>
                           <mrow>
                              <mi class=qopname>sin</mi>
                              <mo>⁡</mo>
                              <mi>𝜃</mi>
                              <mo class=MathClass-bin>−</mo>
                              <mi class=qopname>sin</mi>
                              <mo>⁡</mo>
                              <msub>
                                 <mrow>
                                    <mi>𝜃</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>0</mn>
                                 </mrow>
                              </msub>
                           </mrow>
                           <mo form=postfix fence=true>)</mo>
                        </mrow>
                     </mrow>
                     <mo form=postfix fence=true>]</mo>
                  </mrow>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(11)
</table>
<p>
   It is plotted in <a href=# data-img-alt="Figure 4. Directivity function at 1 kHz with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>d</mi><mo class=MathClass-rel>=</mo><mn>5</mn></math> cm (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>d</mi><mo class=MathClass-bin>∕</mo><mi>λ</mi><mo class=MathClass-rel>≃</mo><mn>0</mn><mo class=MathClass-punc>.</mo><mn>15</mn></math>) for several <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>N</mi></math> values." data-img-src=${config.ASSETS_URL}/04/27/theory/figure-4.svg>Figure 4</a> for non-zero steering angles. By changing the value of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>𝜃</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>, it is possible to steer the array to a targeted angle. One can note that the main lobe width is increasing with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>𝜃</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>. Also, for a too large microphone spacing, aliasing creates large amplitude lobes at other directions.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 4. Directivity function at 1 kHz with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>d</mi><mo class=MathClass-rel>=</mo><mn>5</mn></math> cm (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>d</mi><mo class=MathClass-bin>∕</mo><mi>λ</mi><mo class=MathClass-rel>≃</mo><mn>0</mn><mo class=MathClass-punc>.</mo><mn>15</mn></math>) for several <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>N</mi></math> values." src=${config.ASSETS_URL}/04/27/theory/figure-4.svg><figcaption class=caption>Figure 4. Directivity function at 1 kHz with <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>d</mi><mo class=MathClass-rel>=</mo><mn>5</mn></math> cm (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>d</mi><mo class=MathClass-bin>∕</mo><mi>λ</mi><mo class=MathClass-rel>≃</mo><mn>0</mn><mo class=MathClass-punc>.</mo><mn>15</mn></math>) for several <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>N</mi></math> values.</figcaption></figure>
<p>
   Side lobes can be troublesome because they allow sources at other angles to interfere with the signal coming from the direction being pointed. To lower the side lobe levels, the microphone signals are multiplied by an amplitude factor
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>w</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   calculated using a window function. The upper plot of <a href=# data-img-alt="Figure 5. Upper graph: weighting functions for <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>11</mn></math> microphones, lower graph: effect of the weighting function on the directivity pattern for <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>N</mi><mo class=MathClass-rel>=</mo><mn>11</mn></math>, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>d</mi><mo class=MathClass-rel>=</mo><mn>10</mn></math> cm and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>f</mi><mo class=MathClass-rel>=</mo><mn>1000</mn></math> Hz." data-img-src=${config.ASSETS_URL}/04/27/theory/figure-5.svg>Figure 5</a> gives
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>w</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   for three classic windows:
<ul class=itemize1>
   <li class=itemize>
      Rectangular:
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>w</mi>
         <mo class=MathClass-open>(</mo>
         <mi>n</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mn>1</mn>
      </math>
   <li class=itemize>
      Hann:
      <span style="font-size: 15px">
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>w</mi>
         <mo class=MathClass-open>(</mo>
         <mi>n</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mn>0</mn>
         <mo class=MathClass-punc>.</mo>
         <mn>5</mn>
         <mrow>
            <mo form=prefix fence=true>[</mo>
            <mrow>
               <mn>1</mn>
               <mo class=MathClass-bin>+</mo>
               <mi class=qopname>cos</mi>
               <mo>⁡</mo>
               <mrow>
                  <mo form=prefix fence=true>(</mo>
                  <mrow>
                     <mfrac>
                        <mrow>
                           <mn>2</mn>
                           <mi mathvariant=italic>πn</mi>
                        </mrow>
                        <mrow>
                           <mi>N</mi>
                           <mo class=MathClass-bin>−</mo>
                           <mn>1</mn>
                        </mrow>
                     </mfrac>
                  </mrow>
                  <mo form=postfix fence=true>)</mo>
               </mrow>
            </mrow>
            <mo form=postfix fence=true>]</mo>
         </mrow>
      </math>
      </span>
   <li class=itemize>
      Blackman:
      <span style="font-size: 10px">
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>w</mi>
         <mo class=MathClass-open>(</mo>
         <mi>n</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mn>0</mn>
         <mo class=MathClass-punc>.</mo>
         <mn>42</mn>
         <mo class=MathClass-bin>+</mo>
         <mn>0</mn>
         <mo class=MathClass-punc>.</mo>
         <mn>5</mn>
         <mi class=qopname>cos</mi>
         <mo>⁡</mo>
         <mrow>
            <mo form=prefix fence=true>(</mo>
            <mrow>
               <mfrac>
                  <mrow>
                     <mn>2</mn>
                     <mi mathvariant=italic>πn</mi>
                  </mrow>
                  <mrow>
                     <mi>N</mi>
                     <mo class=MathClass-bin>−</mo>
                     <mn>1</mn>
                  </mrow>
               </mfrac>
            </mrow>
            <mo form=postfix fence=true>)</mo>
         </mrow>
         <mo class=MathClass-bin>+</mo>
         <mn>0</mn>
         <mo class=MathClass-punc>.</mo>
         <mn>08</mn>
         <mi class=qopname>cos</mi>
         <mo>⁡</mo>
         <mrow>
            <mo form=prefix fence=true>(</mo>
            <mrow>
               <mfrac>
                  <mrow>
                     <mn>4</mn>
                     <mi mathvariant=italic>πn</mi>
                  </mrow>
                  <mrow>
                     <mi>N</mi>
                     <mo class=MathClass-bin>−</mo>
                     <mn>1</mn>
                  </mrow>
               </mfrac>
            </mrow>
            <mo form=postfix fence=true>)</mo>
         </mrow>
      </math>
      </span>
</ul>
<p>
   The effect of the window function on the directivity pattern is highlighted in the lower plot of <a href=# data-img-alt="Figure 5. Upper graph: weighting functions for <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>11</mn></math> microphones, lower graph: effect of the weighting function on the directivity pattern for <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>N</mi><mo class=MathClass-rel>=</mo><mn>11</mn></math>, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>d</mi><mo class=MathClass-rel>=</mo><mn>10</mn></math> cm and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>f</mi><mo class=MathClass-rel>=</mo><mn>1000</mn></math> Hz." data-img-src=${config.ASSETS_URL}/04/27/theory/figure-5.svg>Figure 5</a>. Thanks to the windowing, the side lobes are considerably attenuated at the expense of a broadening of the main lobe.
   <figure class=figure>
      <img alt="Figure 5. Upper graph: weighting functions for <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mn>11</mn></math> microphones, lower graph: effect of the weighting function on the directivity pattern for <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>N</mi><mo class=MathClass-rel>=</mo><mn>11</mn></math>, <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>d</mi><mo class=MathClass-rel>=</mo><mn>10</mn></math> cm and <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi>f</mi><mo class=MathClass-rel>=</mo><mn>1000</mn></math> Hz." src=${config.ASSETS_URL}/04/27/theory/figure-5.svg>
      <figcaption class=caption>
         Figure 5. Upper graph: weighting functions for 11 microphones, lower graph: effect of the weighting function on the directivity pattern for
         <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>N</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>11</mn>
         </math>,
         <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>d</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
         </math>
         cm and
         <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>f</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>1000</mn>
         </math>
         Hz.
      </figcaption>
   </figure>
<h3 class=sectionHead>Differential arrays</h3>
<p>
   Delay and sum beamforming can work very well for a large number of microphones. This method is also easy to implement. However, it has some shortcomings, such as the presence of high amplitude side lobes for small
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>N</mi>
   </math>. In addition, the directivity pattern of the antenna is highly frequency dependent. To overcome the latter, differential microphone arrays can be used [2]. They are mainly used for consumer electronics such as smartphones, smart speakers, teleconferencing systems, etc. because they meet the following constraints:
<ul class=itemize1>
   <li class=itemize>small size,
   <li class=itemize>low cost,
   <li class=itemize>low number of microphones,
   <li class=itemize>frequency independant directivity patterns,
   <li class=itemize>high directivity factors for small arrays.
</ul>
<p>
   “Delay & sum” beamforming is based on the summation of the microphone signals, while for differential arrays, the subtraction of the microphone signals is used to get the desired beampattern. Let’s start with a basic example to explain the concept: the two-microphone antenna which is depicted in <a href=# data-img-alt="Figure 6. Two-microphone antenna." data-img-src=${config.ASSETS_URL}/04/27/theory/figure-6.svg>Figure 6</a>. Let’s consider a plane wave propagating with a
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>𝜃</mi>
   </math>
   angle to the antenna axis. The path difference between the 2 microphones is equal to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>δr</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>d</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>, where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>d</mi>
   </math>
   is the microphone spacing. Then, the pressure
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   on the second microphone can be expressed as a function of the pressure on the first microphone
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>jkδr</mi>
         </mrow>
      </msup>
   </math>
   yielding
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>jkδr</mi>
         </mrow>
      </msup>
   </math>. At low frequencies (<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>λ</mi>
      <mo class=MathClass-rel>≫</mo>
      <mi>e</mi>
   </math>),
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>jkδr</mi>
         </mrow>
      </msup>
      <mo class=MathClass-rel>≃</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>−</mo>
      <mi mathvariant=italic>jkδr</mi>
   </math>. Then, we get
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>jkδr</mi>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>jkd</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>. Note that the pressure difference is proportional to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>. The directivity of a bidirectional microphone is found.<br class=newline>
   <figure class=figure>
      <img alt="Figure 6. Two-microphone antenna." src=${config.ASSETS_URL}/04/27/theory/figure-6.svg>
      <figcaption class=caption>Figure 6. Two-microphone antenna.</figcaption>
   </figure>
<p>
   By filtering the signal of a microphone, it is possible to obtain other polar patterns: hypocardioid, cardioid, hypercardioid, etc. Using a larger number of microphones allows to obtain higher order directivity patterns. For example, 3 microphones are required to obtain a second order function with a
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mi>𝜃</mi>
   </math>
   term, and even more microphones for higher orders. This results in narrower and more complex directivities. A particularly interesting result is that the null angles are not frequency dependent, at least below aliasing effects occur. It is then possible to place these angles in the direction of the concurrent sources to mute them in the output signal.<br class=newline>To obtain a desired directivity function, the weights (amplitude and phase) to be applied to each microphone signal must then be calculated according to the antenna geometry (linear, cylindrical, any, etc.). Drawbacks of the differential arrays are that:
<ul class=itemize1>
   <li class=itemize>the weighting coefficients depend on frequency, however, it is possible to calculate the impulse response of the beamforming filter by calculating the inverse Fourier transform of the weighting coefficients,
   <li class=itemize>
      the response is proportional to
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msup>
            <mrow>
               <mi>ω</mi>
            </mrow>
            <mrow>
               <mi>n</mi>
            </mrow>
         </msup>
      </math>, where
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>n</mi>
      </math>
      is the order. Its compensation amplifies the noise at low frequency. In practice, it is difficult to use orders greater than 2.
</ul>
<p>Despite these shortcomings, differential microphone arrays remain a good solution for low order beam patterns and compact antennas.
<h3 class=sectionHead>Conclusion</h3>
<p>In this Lesson, the principles of delay and sum and differential beamforming were presented, highlighting their pros and cons. Nevertheless, the world of array processing, driven by connected objects, offers many other more or less complex algorithms including:
<ul class=itemize1>
   <li class=itemize>inverse methods,
   <li class=itemize>correlation methods,
   <li class=itemize>constrained optimisation,
   <li class=itemize>high resolution methods (MUSIC [5], ESPRIT [4], etc.),
   <li class=itemize>high-order signal statistics based arrays,
   <li class=itemize>deep learning or neural network based arrays.
</ul>
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>ESPRIT
  <dd>Estimation of Signal Parameters via Rational Invariance Techniques
  <dt>MUSIC
  <dd>MUltiple SIgnal Classification
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-benesty2008microphone>[1]
   <dd class=thebibliography id=bib-1>
      <p>Jacob Benesty, Jingdong Chen, and Yiteng Huang. <span class=cmti-10>Microphone array </span><span class=cmti-10>signal processing</span>. Vol. 1. Springer Science & Business Media, 2008. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9783540786122.
   <dt class=thebibliography id=X0-benesty2012study>[2]
   <dd class=thebibliography id=bib-2>
      <p>Jacob Benesty and Chen Jingdong. <span class=cmti-10>Study and design of differential </span><span class=cmti-10>microphone arrays</span>. Vol. 6. Springer Science & Business Media, 2012. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 9783642337529.
   <dt class=thebibliography id=X0-christensen2004beamforming>[3]
   <dd class=thebibliography id=bib-3>
      <p>JJ Christensen and J Hald. “Beamforming, Technical review”. In: <span class=cmti-10>Brüel&Kjær Sound&Vibration Measurement A/S </span>1 (2004).
   <dt class=thebibliography id=X0-roy1989esprit>[4]
   <dd class=thebibliography id=bib-4>
      <p>Richard Roy and Thomas Kailath. “ESPRIT-estimation of signal parameters via rotational invariance techniques”. In: <span class=cmti-10>IEEE Transactions </span><span class=cmti-10>on acoustics, speech, and signal processing </span>37.7 (1989), pp. 984–995.
   <dt class=thebibliography id=X0-schmidt1986multiple>[5]
   <dd class=thebibliography id=bib-5>
      <p>Ralph Schmidt. “Multiple emitter location and signal parameter estimation”. In: <span class=cmti-10>IEEE transactions on antennas and propagation </span>34.3 (1986), pp. 276–280.
</dl>`;

export default theory;
